import React from 'react'
import { User } from '@/types/auth'
import { SubscriptionResponse } from '@/types/subscription'
import { ShieldCheckIcon, CreditCardIcon, SparklesIcon } from '@heroicons/react/24/solid'
import { cn } from '@/utils/cn'

interface SubscriptionBadgeProps {
    user: User | null
    subscription?: SubscriptionResponse | null
    compact?: boolean
    showBg?: boolean
    className?: string
}

export const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({
    user,
    subscription,
    compact = false,
    showBg = true,
    className
}) => {
    // Determine tier from subscription or user
    const tier = subscription?.tier || user?.subscription?.tier || 'free'
    const status = subscription?.status || user?.subscription?.status || 'inactive'

    const tierConfig = {
        free: {
            label: 'Free',
            color: 'text-gray-600 dark:text-gray-400',
            bgColor: 'bg-gray-100 dark:bg-gray-800',
            borderColor: 'border-gray-200 dark:border-gray-700',
            icon: null,
            badge: '🆓'
        },
        pro: {
            label: 'Pro Member',
            color: 'text-blue-700 dark:text-blue-300',
            bgColor: 'bg-blue-100 dark:bg-blue-900/30',
            borderColor: 'border-blue-200 dark:border-blue-800',
            icon: SparklesIcon,
            badge: '⭐'
        },
        enterprise: {
            label: 'Enterprise',
            color: 'text-amber-700 dark:text-amber-300',
            bgColor: 'bg-amber-100 dark:bg-amber-900/30',
            borderColor: 'border-amber-200 dark:border-amber-800',
            icon: CreditCardIcon,
            badge: '👑'
        },
        business: {
            label: 'Business',
            color: 'text-green-700 dark:text-green-300',
            bgColor: 'bg-green-100 dark:bg-green-900/30',
            borderColor: 'border-green-200 dark:border-green-800',
            icon: ShieldCheckIcon,
            badge: '✓'
        }
    }

    const config = tierConfig[tier as keyof typeof tierConfig] || tierConfig.free
    const Icon = config.icon

    // Don't show badge if free and inactive
    if (tier === 'free' && status !== 'active') {
        return null
    }

    if (compact) {
        return (
            <span className={cn('text-sm font-medium', config.color, className)}>
                {config.badge} {config.label}
            </span>
        )
    }

    return (
        <div
            className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border',
                showBg ? `${config.bgColor} ${config.borderColor}` : 'bg-transparent border-transparent',
                config.color,
                className
            )}
        >
            {Icon && <Icon className="w-4 h-4" />}
            <span>{config.badge} {config.label}</span>
        </div>
    )
}